/**
 * The contact form's shape and rules.
 *
 * 🔑 VALIDATION LIVES HERE, NOT IN THE COMPONENT, for the same reason the
 * operator application's does: the rules are testable without a browser, and
 * when the form is finally wired to a transport the sender can reuse them
 * rather than re-deriving a second, subtly different set.
 */
export interface ContactMessage {
  name: string;
  email: string;
  organisation: string;
  subject: string;
  message: string;
}

export const EMPTY_CONTACT: ContactMessage = {
  name: "", email: "", organisation: "", subject: "", message: "",
};

/**
 * The three mailboxes. All exist — ankhos.io has an MX record (Google
 * Workspace), so these receive.
 *
 * 🔑 SUPPORT IS THE DEFAULT AND THE FALLBACK. A visitor who guesses wrong must
 * still reach someone; routing exists to save a forward, never to bounce a
 * message. Anything not matched below goes to support.
 */
export const SUPPORT_EMAIL = "support@ankhos.io";
export const BILLING_EMAIL = "billing@ankhos.io";
export const SALES_EMAIL = "sales@ankhos.io";

export const MAILBOXES = [
  { address: SALES_EMAIL, purpose: "Plans and pricing, registering as a provider, and adding a service to an account you already have" },
  { address: BILLING_EMAIL, purpose: "Invoices, payments and anything on a bill" },
  { address: SUPPORT_EMAIL, purpose: "Everything else once you are running, including technical problems and data requests" },
] as const;

export const CONTACT_SUBJECTS = [
  "Sales and pricing",
  "Registering as a provider",
  "Adding a service to my account",
  "Billing or an invoice",
  "Technical problem",
  "Data protection or a grievance",
  "Something else",
] as const;

/**
 * Which mailbox a subject belongs to.
 *
 * ⚠️ A data-protection request goes to SUPPORT, not to a legal address, because
 * DPDP timelines start when the request ARRIVES. Pointing it at a mailbox nobody
 * watches daily would start a statutory clock in an inbox that is not read.
 */
const SUBJECT_ROUTE: Record<string, string> = {
  "Sales and pricing": SALES_EMAIL,
  "Registering as a provider": SALES_EMAIL,
  // An EXISTING customer buying MORE is a sales conversation, not a support
  // ticket. Without this line it falls through to support and costs a forward.
  "Adding a service to my account": SALES_EMAIL,
  "Billing or an invoice": BILLING_EMAIL,
};

export function routeFor(subject: string): string {
  return SUBJECT_ROUTE[subject] ?? SUPPORT_EMAIL;
}

/**
 * Deliberately permissive: one @, something either side, a dot in the domain.
 *
 * 🔑 A stricter regex rejects real addresses — RFC 5321 permits far more than
 * people expect — and the only thing that proves an address is a message
 * arriving at it. This rejects what CANNOT be an address, nothing more.
 */
export function isPlausibleEmail(v: string): boolean {
  const s = v.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) && !s.includes("..");
}

/** The minimum that makes a message worth a reply. */
export const MESSAGE_MIN = 20;

export function validateContact(m: ContactMessage): string[] {
  const p: string[] = [];
  if (!m.name.trim()) p.push("Your name is required.");
  if (!m.email.trim()) p.push("An email address is required — it is how we reply.");
  else if (!isPlausibleEmail(m.email)) p.push("That does not look like an email address.");
  if (!m.subject) p.push("Choose what this is about.");
  const body = m.message.trim();
  if (!body) p.push("A message is required.");
  else if (body.length < MESSAGE_MIN)
    p.push(`Please say a little more — at least ${MESSAGE_MIN} characters.`);
  return p;
}

/**
 * A mailto: fallback carrying everything already typed.
 *
 * 🔑 THIS IS THE PATH THAT ACTUALLY WORKS. There is no transport behind the
 * form — MSG91 is not configured — so a form that merely said "sent" would be
 * a lie, and one that offered nothing else would waste what the visitor typed.
 * Encoded with encodeURIComponent so newlines, ampersands and non-ASCII survive.
 */
export function mailtoHref(m: ContactMessage): string {
  const subject = m.subject ? `[AnkhOS] ${m.subject}` : "[AnkhOS] Enquiry";
  const to = routeFor(m.subject);
  const lines = [
    m.message.trim(),
    "",
    "---",
    `Name: ${m.name.trim()}`,
    m.organisation.trim() ? `Organisation: ${m.organisation.trim()}` : "",
    `Reply to: ${m.email.trim()}`,
  ].filter(Boolean);
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
}
